import { useState, useEffect, useCallback } from 'react';
import { automationsAPI, integrationsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import {
  Zap, Play, Clock, Shield, Save, Plus, Trash2, Loader2,
  Phone, MessageSquare, Mail
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const CHANNELS = ['sms', 'whatsapp', 'email'];

export default function AutomationsPage() {
  const [rules, setRules] = useState([]);
  const [globalLimits, setGlobalLimits] = useState({ global_max_messages_per_lead_per_day: 3, global_max_messages_per_lead_per_week: 10 });
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState({ sms: [], whatsapp: [], email: [] });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [savingLimits, setSavingLimits] = useState(false);
  const { user } = useAuth();
  const { formatDateTime } = useSettings();
  const isAdmin = user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const [rr, lr, tr, er] = await Promise.all([
        automationsAPI.getRules(),
        automationsAPI.getLog({ limit: 20 }),
        integrationsAPI.get().catch(() => ({ data: {} })),
        integrationsAPI.getEmailConfig().catch(() => ({ data: {} })),
      ]);
      setRules(rr.data.rules || []);
      setGlobalLimits(rr.data.global_limits || {});
      setLogs(lr.data.logs || []);
      setTemplates({
        sms: tr.data.sms_templates || [],
        whatsapp: tr.data.whatsapp_templates || [],
        email: er.data.templates || [],
      });
    } catch (e) {  } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!isAdmin) {
    return (
      <div className="space-y-5 fade-in" data-testid="automations-page">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Automatisierung</h1>
        <Card className="border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="p-8 text-center text-muted-foreground">
            <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Nur Administratoren können Automatisierungen verwalten.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) return <div className="h-64 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5 fade-in max-w-4xl" data-testid="automations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Automatisierung</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">Automatische Nachrichten bei überfälligen Nachfassungen</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowLogs(!showLogs)} className="gap-1.5 text-xs">
            <Clock className="w-3.5 h-3.5" /> {showLogs ? 'Protokoll ausblenden' : 'Protokoll anzeigen'}
          </Button>
          <Button
            size="sm"
            onClick={async () => {
              setProcessing(true);
              try {
                const r = await automationsAPI.process();
                toast.success(`${r.data.sent} gesendet, ${r.data.skipped} übersprungen, ${r.data.errors} Fehler`);
                fetchData();
              } catch (e) { toast.error(e.response?.data?.detail || 'Fehlgeschlagen'); }
              finally { setProcessing(false); }
            }}
            disabled={processing || !rules.some(r => r.active)}
            className="gap-1.5 bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="run-automations-btn"
          >
            {processing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            {processing ? 'Läuft...' : 'Jetzt ausführen'}
          </Button>
        </div>
      </div>

      {/* Global Rate Limits */}
      <Card className="border-border/60">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-semibold">Globale Sendelimits</span>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Pro Lead / Tag</Label>
              <Input type="number" min={1} max={20} value={globalLimits.global_max_messages_per_lead_per_day || 3} onChange={(e) => setGlobalLimits(p => ({ ...p, global_max_messages_per_lead_per_day: parseInt(e.target.value) || 3 }))} className="w-16 h-7 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Pro Lead / Woche</Label>
              <Input type="number" min={1} max={50} value={globalLimits.global_max_messages_per_lead_per_week || 10} onChange={(e) => setGlobalLimits(p => ({ ...p, global_max_messages_per_lead_per_week: parseInt(e.target.value) || 10 }))} className="w-16 h-7 text-xs" />
            </div>
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={async () => { setSavingLimits(true); try { await automationsAPI.updateGlobalLimits(globalLimits); toast.success('Gespeichert'); } catch (e) {  } finally { setSavingLimits(false); } }} disabled={savingLimits}>
              <Save className="w-3 h-3" /> Speichern
            </Button>
            <span className="text-[10px] text-muted-foreground">Feste Obergrenze über alle Kanäle.</span>
          </div>
        </CardContent>
      </Card>

      {/* Rules */}
      {rules.map((rule) => {
        const channelTemplates = templates[rule.channel] || [];
        const ICONS = { sms: Phone, whatsapp: MessageSquare, email: Mail };
        const COLORS = { sms: 'text-blue-500', whatsapp: 'text-green-500', email: 'text-indigo-500' };
        const Icon = ICONS[rule.channel] || Phone;

        const updateRule = async (updates) => {
          await automationsAPI.updateRule(rule.id, updates);
          setRules(p => p.map(r => r.id === rule.id ? { ...r, ...updates } : r));
        };

        return (
          <Card key={rule.id} className={`border-border/60 ${rule.active ? 'border-amber-300/50 dark:border-amber-800/50' : ''}`} data-testid={`rule-${rule.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Switch
                  checked={rule.active}
                  onCheckedChange={async (v) => {
                    await updateRule({ active: v });
                    toast.success(v ? 'Aktiviert' : 'Deaktiviert');
                  }}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{rule.name}</span>
                    {rule.active && <Badge className="bg-amber-500 text-white text-[8px]">Aktiv</Badge>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <Select
                      value={rule.channel}
                      onValueChange={async (channel) => {
                        const nextTemplate = (templates[channel] || [])[0]?.id || '';
                        await updateRule({ channel, template_id: nextTemplate });
                        toast.success('Kanal aktualisiert');
                      }}
                    >
                      <SelectTrigger className={`h-6 w-28 text-[10px] gap-1 ${COLORS[rule.channel]}`} data-testid={`rule-channel-${rule.id}`}>
                        <Icon className="w-2.5 h-2.5" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CHANNELS.map(c => <SelectItem key={c} value={c} className="text-xs uppercase">{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select
                      value={rule.template_id}
                      onValueChange={async (template_id) => {
                        await updateRule({ template_id });
                        toast.success('Vorlage aktualisiert');
                      }}
                    >
                      <SelectTrigger className="h-6 w-40 text-[10px]" data-testid={`rule-template-${rule.id}`}>
                        <SelectValue placeholder="Vorlage wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {channelTemplates.map(t => <SelectItem key={t.id} value={t.id} className="text-xs">{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <span className="text-[10px] bg-muted px-2 py-0.5 rounded-full">Überfällig ab {rule.trigger_config?.overdue_by_minutes || 60} Min.</span>
                    <span className="text-[10px] bg-muted px-2 py-0.5 rounded-full">Max. {rule.max_per_lead_per_day}/Lead/Tag</span>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    await automationsAPI.deleteRule(rule.id);
                    setRules(p => p.filter(r => r.id !== rule.id));
                    toast.success('Gelöscht');
                  }}
                  className="text-muted-foreground hover:text-destructive p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Button
        variant="outline"
        onClick={async () => {
          const r = await automationsAPI.createRule({ name: 'Neue Regel', trigger_type: 'overdue_followup', trigger_config: { overdue_by_minutes: 60 }, channel: 'sms', template_id: 'follow_up', max_per_lead_per_day: 1 });
          setRules(p => [...p, r.data]);
          toast.success('Erstellt');
        }}
        className="gap-2 w-full border-dashed"
        data-testid="add-rule-btn"
      >
        <Plus className="w-4 h-4" /> Automatisierungsregel hinzufügen
      </Button>

      {/* Execution Log */}
      {showLogs && (
        <Card className="border-border/60">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Ausführungsprotokoll</CardTitle></CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">Noch keine Ausführungen. Auf &quot;Jetzt ausführen&quot; klicken, um zu starten.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {logs.map((log) => (
                  <div key={log.id} className="flex items-start gap-3 py-2 border-b border-border/30 last:border-0 text-xs">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${log.status === 'sent' ? 'bg-green-500' : log.status === 'error' ? 'bg-red-500' : 'bg-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{log.lead_name} <span className="text-muted-foreground font-normal">über {log.channel?.toUpperCase()}</span></p>
                      <p className="text-muted-foreground truncate">{log.message || log.error || log.rule_name}</p>
                    </div>
                    <span className="text-[10px] text-muted-foreground mono flex-shrink-0">{formatDateTime(log.executed_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
